import { isObject } from "lodash";
import { CamelCasedPropertiesDeep } from "type-fest";

// Simple camelCase implementation that works in Edge Runtime
function camelCase(str: string): string {
  return str.replace(/[_-]([a-z0-9])/g, (_, letter: string) => letter.toUpperCase());
}

const keyTransforms = {
  uuid: "id",
  sectionUuid: "sectionId",
  kitUuid: "kitId",
  itemUuid: "itemId",
  portalUuid: "portalId",
  assetUuid: "assetId",
  sourceUuid: "sourceId",
} as const;

type Convert<Key> = Key extends keyof typeof keyTransforms ? (typeof keyTransforms)[Key] : Key;

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
type Converted<T> = T extends Function
  ? T
  : T extends Array<infer U>
    ? Array<Converted<U>>
    : T extends Set<infer U>
      ? Set<Converted<U>>
      : {
          [Key in keyof T as Convert<Key>]: Converted<T[Key]>;
        };

export type Camelized<T> = Converted<CamelCasedPropertiesDeep<T>>;

const ignoreKeys = ["upgradePermissions"];

function camelize<T extends object>(obj: T): Camelized<T> {
  if (obj === null || obj === undefined) return obj as Camelized<T>;
  return Object.keys(obj).reduce((acc, key) => {
    let _key = key;
    if (typeof key === "string" && !key.startsWith("__")) {
      const camelKey = camelCase(key);
      _key = keyTransforms[camelKey] || camelKey;
    }

    const value = obj[key];
    if (Array.isArray(value)) {
      acc[_key] = value.map(item => (isObject(item) ? camelize(item) : item));
    } else if (isObject(value) && !ignoreKeys.includes(_key)) {
      acc[_key] = camelize(value);
    } else {
      acc[_key] = value;
    }
    return acc;
  }, {} as Camelized<T>);
}
export default camelize;
